import NotificationsActiveOutlined from '@mui/icons-material/NotificationsActiveOutlined'
import { Alert, Button, Stack } from '@mui/material'
import useLocalStorage from '@/hooks/useLocalStorage'
import usePushNotifications from '@/hooks/usePushNotifications'

// "Not now" hides the prompt for a while rather than for good, since there is
// no other place in the app to turn push on.
const SNOOZE_MS = 30 * 24 * 60 * 60 * 1000

/**
 * The in-app prompt for browser push. The browser's own permission dialog
 * only opens from "Turn on" here, never on its own. Mounting this also keeps
 * a browser that already allowed push registered (see usePushNotifications).
 */
export default function PushNotificationsBanner() {
  const { permission, enable } = usePushNotifications()
  // Permission belongs to the browser, so the snooze isn't kept per account.
  const [snoozedAt, setSnoozedAt] = useLocalStorage<number | null>(
    'push-prompt-snoozed-at',
    null,
    false,
  )

  if (permission !== 'default') return null
  if (snoozedAt && Date.now() - snoozedAt < SNOOZE_MS) return null

  return (
    <Alert
      severity="info"
      icon={<NotificationsActiveOutlined />}
      sx={{ borderRadius: 0, alignItems: 'center' }}
      action={
        <Stack direction="row" spacing={1}>
          <Button
            variant="text"
            color="inherit"
            onClick={() => setSnoozedAt(Date.now())}
          >
            Not now
          </Button>
          <Button
            onClick={() => {
              enable().catch((error) => {
                console.error(
                  'Could not ask for notification permission',
                  error,
                )
              })
            }}
          >
            Turn on
          </Button>
        </Stack>
      }
    >
      Turn on browser notifications to hear about documents that need you, even
      when Pixydocs isn&apos;t open.
    </Alert>
  )
}
